import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useConfirm } from '../contexts/ConfirmContext'
import PageShell from '../components/Layout/PageShell'
import UpgradeModal from '../components/UpgradeModal'
import axios from 'axios'
import toast from 'react-hot-toast'
import { useChatSocket } from '../hooks/useChatSocket'
import { useTrtcCall } from '../hooks/useTrtcCall'
import TrtcCallOverlay from '../components/TrtcCallOverlay'
import { getChatAvatarUrl, getContactInitials, getAvatarBg } from '../utils/chatAvatars'
import { formatUserDisplayName } from '../utils/userDisplayName'
import { languageLabel } from '../constants/preferredLanguages'
import { RsIcon, IconLabel, ModalClose } from '../components/icons'
import ChatImageAttachment from '../components/Chat/ChatImageAttachment'
import ChatQuoteBlock from '../components/Chat/ChatQuoteBlock'
import { buildPasteImageFilename, isImageFileName } from '../utils/chatFiles'
import { buildQuoteFromMessage, mapReplyFromRow } from '../utils/chatQuote'
import { isSessionTimeoutError } from '../utils/sessionTimeout'
import { likelyAlreadyInTargetLanguage } from '../utils/translationLanguage'
import '../styles/chat.css'

function msgTranslationKey(id) {
  return String(id)
}

const TRANSLATION_ERROR_PREFIXES = [
  '[DeepSeek API Key not configured]',
  '[Translation unavailable]',
  '[DeepSeek account balance insufficient',
  '[DeepSeek rate limit exceeded',
  '[DeepSeek API Key invalid or expired]',
]

function isTranslationError(text) {
  const t = String(text || '').trim()
  return TRANSLATION_ERROR_PREFIXES.some((prefix) => t.startsWith(prefix))
}

/** Skip showing translation when it matches the original (including minor rephrasing). */
function isRedundantTranslation(original, translated) {
  const normalize = (s) =>
    String(s || '')
      .toLowerCase()
      .replace(/[^\w\s\u4e00-\u9fff]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
  const a = normalize(original)
  const b = normalize(translated)
  if (!a || !b) return true
  if (a === b) return true
  const wordsA = a.split(' ').filter(Boolean)
  const wordsB = b.split(' ').filter(Boolean)
  if (!wordsA.length || !wordsB.length) return a === b
  const setB = new Set(wordsB)
  const shared = wordsA.filter((w) => setB.has(w)).length
  const overlap = shared / Math.max(wordsA.length, wordsB.length)
  return overlap >= 0.85
}

const EMOJIS = [
  '😀', '😃', '😄', '😁', '😆', '😅', '🤣', '😂',
  '🙂', '😉', '😊', '😇', '🥰', '😍', '🤩', '😘',
  '👍', '👎', '👏', '🙌', '🤝', '🙏', '💪', '❤️',
  '✨', '⭐', '🌟', '💯', '🔥', '👀', '🎉', '✅',
]

const MAX_GROUP_MEMBERS = 15

function formatFileSize(bytes) {
  if (!bytes) return '—'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function fileIcon(name) {
  const ext = (name || '').split('.').pop()?.toLowerCase()
  if (['xls', 'xlsx', 'csv'].includes(ext)) return 'spreadsheet'
  if (['doc', 'docx'].includes(ext)) return 'fileText'
  if (['ppt', 'pptx'].includes(ext)) return 'templates'
  if (['pdf'].includes(ext)) return 'fileText'
  return 'folder'
}

const OTHER_CTX_ITEMS = [
  { icon: 'copy', label: 'Copy', action: 'copy' },
  { icon: 'expand', label: 'Expand', action: 'expand' },
  { icon: 'externalLink', label: 'Forward', action: 'forward' },
  { icon: 'star', label: 'Favorite', action: 'favorite' },
  { icon: 'messageSquare', label: 'Quote', action: 'quote' },
  { sep: true },
  { icon: 'trash', label: 'Delete', action: 'delete', danger: true },
]

const SELF_CTX_ITEMS = [
  { icon: 'copy', label: 'Copy', action: 'copy' },
  { icon: 'expand', label: 'Expand', action: 'expand' },
  { icon: 'messageSquare', label: 'Quote', action: 'quote' },
  { sep: true },
  { icon: 'moderation', label: 'Recall', action: 'recall', danger: true },
]

const AVATAR_COLORS = {
  GP: '#1d4ed8',
  LP: '#16a34a',
  Startup: '#ea580c',
  ServiceProvider: '#7c3aed',
  Admin: '#dc2626',
}

function avatarColor(role) {
  return AVATAR_COLORS[role] || '#64748b'
}

function initials(name) {
  return (name || '?').split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase()
}

function convDisplayName(contact) {
  return formatUserDisplayName(contact)
}

function formatConvTime(timeStr, rawDate) {
  if (!timeStr && !rawDate) return ''
  if (rawDate) {
    const d = new Date(rawDate)
    const now = new Date()
    if (d.toDateString() === now.toDateString()) {
      return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    }
    if (d.getFullYear() === now.getFullYear()) {
      return d.toLocaleDateString([], { month: '2-digit', day: '2-digit' }).replace(/\//g, '/')
    }
    return d.toLocaleDateString([], { year: 'numeric', month: '2-digit', day: '2-digit' })
  }
  return timeStr
}

function extractFileId(fileUrl) {
  if (!fileUrl) return null
  const match = String(fileUrl).match(/\/api\/files\/download\/([^/?#]+)/)
  return match ? match[1] : null
}

function chatMessagePreview(row) {
  if (row.message_type === 'file') return `File: ${row.content || 'File'}`
  return row.content
}

function formatReadStatusLabel(msg, isGroup) {
  if (msg.sender !== 'me' || msg.messageType === 'system') return null
  if (isGroup) {
    const totalCount = msg.readTotal ?? 0
    const readStatus = msg.readStatus || 'unread'
    if (totalCount <= 0) return 'Read'
    if (readStatus === 'read') return 'Read by all'
    if (readStatus === 'partial') return `${msg.readCount ?? 0} read`
    return 'Unread'
  }
  const readStatus = msg.readStatus || (msg.status === 'read' ? 'read' : 'unread')
  return readStatus === 'read' ? 'Read' : 'Unread'
}

function mapMessage(row, myId) {
  const isMe = String(row.sender_id) === String(myId)
  const isSystem = row.message_type === 'system'
  const hasFileUrl = Boolean(row.file_url)
  const legacyFile = !hasFileUrl && /^\[File:\s*.+\]$/i.test(String(row.content || '').trim())
  const isFile = row.message_type === 'file' || hasFileUrl || legacyFile
  const readStatus = row.read_status || (row.is_read ? 'read' : 'unread')
  return {
    id: row.id,
    sender: isSystem ? 'system' : (isMe ? 'me' : 'them'),
    text: row.content,
    contentMasked: !!row.content_masked,
    time: new Date(row.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    createdAt: row.created_at,
    status: readStatus === 'read' ? 'read' : 'sent',
    readStatus,
    readCount: row.read_count ?? 0,
    readTotal: row.read_total ?? 0,
    readAt: row.read_at || null,
    senderName: row.sender_name,
    senderId: row.sender_id,
    messageType: isFile ? 'file' : row.message_type,
    conversationId: row.conversation_id,
    fileUrl: row.file_url || null,
    fileId: extractFileId(row.file_url),
    fileName: isFile ? row.content : null,
    legacyFile,
    isImage: isFile && isImageFileName(row.content),
    replyTo: mapReplyFromRow(row),
    replyToId: row.reply_to_id || null,
  }
}

async function parseDownloadError(err) {
  const data = err?.response?.data
  if (data instanceof Blob) {
    try {
      const json = JSON.parse(await data.text())
      return json.message || 'Download failed'
    } catch {
      return 'Download failed'
    }
  }
  return err?.response?.data?.message || err?.message || 'Download failed'
}

function ChatAvatar({ contact, size = 42, className = '', alt = '' }) {
  const greyed = contact && contact.canChat === false
    && contact.role !== 'ServiceProvider'
    && contact.role !== 'Admin'
  const src = getChatAvatarUrl(contact)
  const initials = getContactInitials(contact)
  const bg = getAvatarBg(contact)
  if (!src) {
    return (
      <div
        className={`rs-chat-conv-avatar rs-chat-conv-avatar-initials${greyed ? ' greyed' : ''}${className ? ` ${className}` : ''}`}
        aria-label={alt || contact?.name || 'Avatar'}
        style={{ width: size, height: size, background: bg, fontSize: Math.round(size * 0.36) }}
      >
        {initials}
      </div>
    )
  }
  return (
    <img
      className={`rs-chat-conv-avatar${greyed ? ' greyed' : ''}${className ? ` ${className}` : ''}`}
      src={src}
      alt={alt || contact?.name || 'Avatar'}
      width={size}
      height={size}
      loading="lazy"
      style={{ width: size, height: size }}
    />
  )
}

function loadPinned() {
  try {
    return JSON.parse(localStorage.getItem('pinnedChats') || '[]')
  } catch {
    return []
  }
}

function loadMuted() {
  try {
    return JSON.parse(localStorage.getItem('mutedChats') || '[]')
  } catch {
    return []
  }
}

function mergeContactsList(apiContacts, platformAdmin, apiGroups = []) {
  const groupIds = new Set((apiGroups || []).map((g) => g.id))
  const withoutGroups = (apiContacts || []).filter((c) => !groupIds.has(c.id))
  const withoutAdminDup = platformAdmin
    ? withoutGroups.filter((c) => c.id !== platformAdmin.id)
    : withoutGroups
  return [...(apiGroups || []), ...(platformAdmin ? [platformAdmin] : []), ...withoutAdminDup]
}

function contactMatchesSearch(contact, query) {
  if (!query) return true
  const q = query.toLowerCase().trim()
  const hay = [
    contact.name,
    contact.real_name,
    contact.system_name,
    contact.company,
    contact.company_short_name,
    contact.company_full_name,
    convDisplayName(contact),
    contact.role,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
  return hay.includes(q)
}

export default function Chat({ embedded = false }) {
  const { user, isPaid, defaultLanguage } = useAuth()
  const confirm = useConfirm()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [contacts, setContacts] = useState([])
  const [selectedContact, setSelectedContact] = useState(null)
  const [messages, setMessages] = useState([])
  const [inputMessage, setInputMessage] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [onlineCount, setOnlineCount] = useState(0)
  const [middleMode, setMiddleMode] = useState('chat')
  const [msgTranslations, setMsgTranslations] = useState({})
  const [translatingMsgId, setTranslatingMsgId] = useState(null)
  const [showPaywall, setShowPaywall] = useState(false)
  const [showNewMenu, setShowNewMenu] = useState(false)
  const [showChatSearch, setShowChatSearch] = useState(false)
  const [chatSearchQuery, setChatSearchQuery] = useState('')
  const [showEmoji, setShowEmoji] = useState(false)
  const [pinnedIds, setPinnedIds] = useState(loadPinned)
  const [mutedIds, setMutedIds] = useState(loadMuted)
  const [detailContact, setDetailContact] = useState(null)
  const [collapsedGroups, setCollapsedGroups] = useState({})
  const [showFilePanel, setShowFilePanel] = useState(false)
  const [userFiles, setUserFiles] = useState([])
  const [filesLoading, setFilesLoading] = useState(false)
  const [uploadingLocalFile, setUploadingLocalFile] = useState(false)
  const [pendingAttachments, setPendingAttachments] = useState([])
  const [quoteDraft, setQuoteDraft] = useState(null)
  const [imagePreview, setImagePreview] = useState(null)
  const [ctxMenu, setCtxMenu] = useState(null)
  const [hiddenMsgIds, setHiddenMsgIds] = useState([])
  const [recalledMsgIds, setRecalledMsgIds] = useState([])
  const [expandText, setExpandText] = useState(null)
  const [showScreenshot, setShowScreenshot] = useState(false)
  const [screenshotPreview, setScreenshotPreview] = useState(null)
  const [screenshotCapturing, setScreenshotCapturing] = useState(false)
  const [screenshotSaving, setScreenshotSaving] = useState(false)
  const [showGroupModal, setShowGroupModal] = useState(false)
  const [groupPickIds, setGroupPickIds] = useState([])
  const [groupName, setGroupName] = useState('')
  const [groupMemberSearch, setGroupMemberSearch] = useState('')
  const [groupModalMode, setGroupModalMode] = useState('create')
  const [creatingGroup, setCreatingGroup] = useState(false)
  const [matchDeal, setMatchDeal] = useState(null)
  const dealMatchHandledRef = useRef(null)
  const [showForwardModal, setShowForwardModal] = useState(false)
  const [readReceiptsEnabled, setReadReceiptsEnabled] = useState(true)
  const readReceiptsEnabledRef = useRef(true)
  const [onlineCountEnabled, setOnlineCountEnabled] = useState(true)
  const onlineCountEnabledRef = useRef(true)
  const [forwardDraft, setForwardDraft] = useState(null)
  const [startingCall, setStartingCall] = useState(false)
  const [incomingCall, setIncomingCall] = useState(null)
  const [acceptingCall, setAcceptingCall] = useState(false)
  const [trtcConfigured, setTrtcConfigured] = useState(null)
  const {
    callState,
    muted,
    cameraOff,
    joinCall,
    endCall,
    toggleMute,
    toggleCamera,
  } = useTrtcCall()
  const [groupMembers, setGroupMembers] = useState([])
  const [groupMembersLoading, setGroupMembersLoading] = useState(false)
  const [showGroupMembersPanel, setShowGroupMembersPanel] = useState(true)
  const [dissolvingGroup, setDissolvingGroup] = useState(false)
  const [leavingGroup, setLeavingGroup] = useState(false)
  const [peerBlockStatus, setPeerBlockStatus] = useState({ blockedByMe: false, blockedMe: false, blocked: false })
  const [blockActionLoading, setBlockActionLoading] = useState(false)
  const messagesEndRef = useRef(null)
  const chatMessagesRef = useRef(null)
  const inputRef = useRef(null)
  const inputAreaRef = useRef(null)
  const localFileInputRef = useRef(null)
  const pendingAttachmentsRef = useRef([])
  const selectedContactRef = useRef(null)
  const msgTranslationsRef = useRef({})
  const translatingIdsRef = useRef(new Set())
  const loadGroupMembersRef = useRef(null)
  const callStateRef = useRef(null)
  const incomingCallRef = useRef(null)
  selectedContactRef.current = selectedContact
  pendingAttachmentsRef.current = pendingAttachments
  msgTranslationsRef.current = msgTranslations
  callStateRef.current = callState
  incomingCallRef.current = incomingCall
  readReceiptsEnabledRef.current = readReceiptsEnabled
  onlineCountEnabledRef.current = onlineCountEnabled

  const { sendMessage: sendViaSocket, emitTyping, joinConversation, isConnected } = useChatSocket({
    token: typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null,
    userId: user?.id,
    enabled: !!user?.id,
    onTrtcCall: (payload) => {
      if (!payload?.callId || callStateRef.current) return
      setIncomingCall(payload)
    },
    onTrtcCallEnded: (payload) => {
      if (!payload?.callId) return
      const ic = incomingCallRef.current
      const cs = callStateRef.current
      if (ic?.callId === payload.callId) {
        setIncomingCall(null)
      }
      if (cs?.callId === payload.callId) {
        endCall()
      }
    },
    onMessage: (row) => {
      if (row.conversation_id) {
        const groupId = row.conversation_id
        const contact = selectedContactRef.current
        const isOpen = contact?.isGroup && contact.id === groupId
        const msg = mapMessage(row, user?.id)
        if (isOpen) {
          setMessages((prev) => (prev.some((m) => m.id === msg.id) ? prev : [...prev, msg]))
          if (row.sender_id !== user?.id) {
            axios.post(`/api/chat/groups/${groupId}/mark-read`).catch(() => {})
            window.dispatchEvent(new Event('rs-chat-unread-changed'))
            window.dispatchEvent(new Event('rs-notifications-changed'))
          }
        }
        setContacts((prev) =>
          prev.map((c) => {
            if (c.id !== groupId) return c
            const incoming = row.sender_id !== user?.id
            return {
              ...c,
              lastMessage: chatMessagePreview(row),
              lastMessageMasked: !!row.content_masked,
              lastTime: 'Just now',
              unread: incoming && !isOpen ? (c.unread || 0) + 1 : isOpen ? 0 : c.unread,
            }
          })
        )
        if (row.sender_id !== user?.id && !isOpen) {
          window.dispatchEvent(new Event('rs-chat-unread-changed'))
          window.dispatchEvent(new Event('rs-notifications-changed'))
        }
        return
      }

      const peerId = row.sender_id === user?.id ? row.receiver_id : row.sender_id
      const contact = selectedContactRef.current
      const isOpen = contact && peerId === contact.id
      if (isOpen) {
        const msg = mapMessage(row, user?.id)
        setMessages((prev) => (prev.some((m) => m.id === msg.id) ? prev : [...prev, msg]))
        if (row.receiver_id === user?.id) {
          axios.post(`/api/chat/mark-read/${peerId}`).catch(() => {})
          window.dispatchEvent(new Event('rs-chat-unread-changed'))
      window.dispatchEvent(new Event('rs-notifications-changed'))
        }
      }
      setContacts((prev) =>
        prev.map((c) => {
          if (c.id !== peerId) return c
          const incoming = row.receiver_id === user?.id && row.sender_id === peerId
          return {
            ...c,
            lastMessage: chatMessagePreview(row),
            lastMessageMasked: !!row.content_masked,
            lastTime: 'Just now',
            unread: incoming && !isOpen ? (c.unread || 0) + 1 : isOpen ? 0 : c.unread,
          }
        })
      )
      if (row.receiver_id === user?.id && !isOpen) {
        window.dispatchEvent(new Event('rs-chat-unread-changed'))
      window.dispatchEvent(new Event('rs-notifications-changed'))
      }
    },
    onMessageRead: (payload) => {
      if (!readReceiptsEnabledRef.current || !payload?.messageIds?.length) return
      const ids = new Set(payload.messageIds.map(String))
      setMessages((prev) => prev.map((m) => {
        if (!ids.has(String(m.id))) return m
        return { ...m, readStatus: 'read', status: 'read', readAt: payload.readAt || m.readAt }
      }))
    },
    onGroupRead: ({ conversationId }) => {
      if (!readReceiptsEnabledRef.current) return
      const contact = selectedContactRef.current
      if (!contact?.isGroup || contact.id !== conversationId) return
      setMessages((prev) => {
        const myIds = prev
          .filter((m) => m.sender === 'me' && m.messageType !== 'system')
          .map((m) => m.id)
        if (myIds.length) {
          axios.post(`/api/chat/groups/${conversationId}/read-stats`, { messageIds: myIds })
            .then((res) => {
              const stats = res.data?.stats || {}
              setMessages((current) => current.map((m) => {
                const s = stats[m.id]
                if (!s) return m
                return {
                  ...m,
                  readStatus: s.readStatus,
                  readCount: s.readCount,
                  readTotal: s.totalCount,
                }
              }))
            })
            .catch(() => {})
        }
        return prev
      })
    },
    onPresence: ({ userId: uid, online }) => {
      setContacts((prev) =>
        prev.map((c) => (c.id === uid ? { ...c, online: online ? 'online' : 'offline' } : c))
      )
    },
    onOnlineCount: ({ count }) => {
      if (!onlineCountEnabledRef.current) return
      setOnlineCount(Math.max(0, Number(count) || 0))
    },
    onGroupCreated: ({ group }) => {
      if (!group?.id) return
      joinConversation(group.id)
      setContacts((prev) => (prev.some((c) => c.id === group.id) ? prev : [group, ...prev]))
    },
    onGroupDissolved: ({ conversationId }) => {
      if (!conversationId) return
      setContacts((prev) => prev.filter((c) => c.id !== conversationId))
      if (selectedContactRef.current?.id === conversationId) {
        setSelectedContact(null)
        setMessages([])
        setGroupMembers([])
        setShowGroupMembersPanel(false)
      }
    },
    onGroupMembersAdded: ({ conversationId, memberCount }) => {
      if (!conversationId) return
      setContacts((prev) => prev.map((c) => (
        c.id === conversationId
          ? { ...c, memberCount, company: `${memberCount} member${memberCount === 1 ? '' : 's'}` }
          : c
      )))
      if (selectedContactRef.current?.id === conversationId) {
        loadGroupMembersRef.current?.(conversationId)
        setSelectedContact((prev) => (prev?.id === conversationId
          ? { ...prev, memberCount, company: `${memberCount} member${memberCount === 1 ? '' : 's'}` }
          : prev))
      }
    },
    onGroupMemberLeft: ({ conversationId, memberCount }) => {
      if (!conversationId) return
      setContacts((prev) => prev.map((c) => (
        c.id === conversationId
          ? { ...c, memberCount, company: `${memberCount} member${memberCount === 1 ? '' : 's'}` }
          : c
      )))
      if (selectedContactRef.current?.id !== conversationId) return
      loadGroupMembersRef.current?.(conversationId)
      setSelectedContact((prev) => (prev?.id === conversationId
        ? { ...prev, memberCount, company: `${memberCount} member${memberCount === 1 ? '' : 's'}` }
        : prev))
      axios.get(`/api/chat/groups/${conversationId}/history`)
        .then((res) => setMessages(res.data.map((row) => mapMessage(row, user?.id))))
        .catch(() => {})
    },
    onGroupLeft: ({ conversationId }) => {
      if (!conversationId) return
      setContacts((prev) => prev.filter((c) => c.id !== conversationId))
      if (selectedContactRef.current?.id === conversationId) {
        setSelectedContact(null)
        setMessages([])
        setGroupMembers([])
        setShowGroupMembersPanel(false)
      }
    },
  })

  const canChat = useCallback((contact) => {
    if (!contact) return false
    if (contact.isGroup) {
      if (user?.role === 'ServiceProvider' || user?.role === 'Admin') return true
      return isPaid
    }
    if (contact.canChat != null) return contact.canChat
    if (user?.role === 'ServiceProvider' || user?.role === 'Admin') return true
    if (contact.role === 'ServiceProvider' || contact.role === 'Admin') return true
    return isPaid
  }, [user?.role, isPaid])

  useEffect(() => {
    if (!selectedContact || selectedContact.isGroup) {
      setPeerBlockStatus({ blockedByMe: false, blockedMe: false, blocked: false })
      return undefined
    }
    let cancelled = false
    axios.get(`/api/chat/block/${selectedContact.id}`)
      .then((res) => {
        if (!cancelled) setPeerBlockStatus(res.data)
      })
      .catch(() => {
        if (!cancelled) setPeerBlockStatus({ blockedByMe: false, blockedMe: false, blocked: false })
      })
    return () => { cancelled = true }
  }, [selectedContact?.id, selectedContact?.isGroup])

  useEffect(() => {
    if (!user?.id) return undefined
    let cancelled = false
    axios.get('/api/chat/config')
      .then((res) => {
        if (!cancelled) {
          setReadReceiptsEnabled(res.data?.readReceiptsEnabled !== false)
          setOnlineCountEnabled(res.data?.onlineCountEnabled !== false)
        }
      })
      .catch(() => {})
    return () => { cancelled = true }
  }, [user?.id])

  useEffect(() => {
    if (!user?.id || !onlineCountEnabled) return undefined
    let cancelled = false
    axios.get('/api/chat/online-stats')
      .then((res) => {
        if (!cancelled && res.data?.enabled !== false) {
          setOnlineCount(Math.max(0, Number(res.data?.onlineCount) || 0))
        }
      })
      .catch(() => {})
    return () => { cancelled = true }
  }, [user?.id, onlineCountEnabled])

  useEffect(() => {
    const loadContacts = async () => {
      try {
        const [contactsRes, groupsRes] = await Promise.all([
          axios.get('/api/users/contacts/list'),
          axios.get('/api/chat/groups').catch(() => ({ data: [] })),
        ])
        const apiContacts = contactsRes.data.contacts || []
        const platformAdmin = contactsRes.data.platformAdmin || null
        const groups = groupsRes.data || []
        setContacts(mergeContactsList(apiContacts, platformAdmin, groups))
      } catch (err) {
        if (isSessionTimeoutError(err)) return
        const status = err?.response?.status
        if (status === 429) {
          toast.error('Too many requests. Please wait a moment and refresh.')
        } else if (status === 401 || status === 403) {
          toast.error('Session expired. Please sign in again.')
        } else {
          toast.error('Failed to load contacts')
        }
      }
    }
    loadContacts()
  }, [user?.id])

  const translateIncomingMessage = useCallback(async (msg) => {
    if (!msg?.text?.trim() || msg.sender !== 'them' || !defaultLanguage) return
    if (msg.messageType === 'file') return
    const key = msgTranslationKey(msg.id)
    if (recalledMsgIds.includes(msg.id)) return
    if (msgTranslationsRef.current[key]) return
    if (translatingIdsRef.current.has(key)) return
    if (likelyAlreadyInTargetLanguage(msg.text, defaultLanguage)) return

    translatingIdsRef.current.add(key)
    setTranslatingMsgId(msg.id)
    try {
      const res = await axios.post('/api/chat/translate', {
        text: msg.text,
        targetLang: defaultLanguage,
        messageId: msg.id,
      })
      if (res.data?.skipped) return
      const translated = String(res.data?.translated || '').trim()
      if (!translated) return
      const original = String(msg.text || '').trim()
      if (isRedundantTranslation(original, translated)) return
      const isError = isTranslationError(translated)
      const tr = {
        code: defaultLanguage,
        label: languageLabel(defaultLanguage),
        text: translated,
        error: isError,
        cached: !!res.data?.cached,
      }
      setMsgTranslations((prev) => (prev[key] ? prev : { ...prev, [key]: [tr] }))
    } catch (err) {
      const errText = err.response?.data?.message || 'Translation unavailable'
      setMsgTranslations((prev) => ({
        ...prev,
        [key]: [{ code: 'error', label: 'Translation', text: errText, error: true }],
      }))
    } finally {
      translatingIdsRef.current.delete(key)
      setTranslatingMsgId((cur) => (cur === msg.id ? null : cur))
    }
  }, [defaultLanguage, recalledMsgIds])

  const applyCachedTranslations = useCallback(async (msgs) => {
    if (!defaultLanguage || !msgs?.length) return
    const incoming = msgs.filter((m) => m.sender === 'them' && m.text?.trim() && m.messageType !== 'file')
    if (!incoming.length) return

    try {
      const res = await axios.post('/api/chat/translations/lookup', {
        messageIds: incoming.map((m) => m.id),
        targetLang: defaultLanguage,
      })
      const translations = res.data?.translations || {}
      const next = {}
      for (const msg of incoming) {
        const text = translations[String(msg.id)]
        if (!text) continue
        if (likelyAlreadyInTargetLanguage(msg.text, defaultLanguage)) continue
        if (isRedundantTranslation(msg.text, text)) continue
        const key = msgTranslationKey(msg.id)
        next[key] = [{
          code: defaultLanguage,
          label: languageLabel(defaultLanguage),
          text,
          error: isTranslationError(text),
          cached: true,
        }]
      }
      if (Object.keys(next).length) {
        setMsgTranslations((prev) => ({ ...prev, ...next }))
      }
    } catch {
      /* cached lookup is best-effort */
    }
  }, [defaultLanguage])

  useEffect(() => {
    if (!defaultLanguage || !selectedContact) return
    messages.forEach((msg) => {
      if (msg.sender === 'them' && msg.text?.trim() && msg.messageType !== 'file' && !recalledMsgIds.includes(msg.id)) {
        translateIncomingMessage(msg)
      }
    })
  }, [messages, defaultLanguage, selectedContact?.id, recalledMsgIds, translateIncomingMessage])

  useEffect(() => {
    setMsgTranslations({})
    translatingIdsRef.current.clear()
    setTranslatingMsgId(null)
  }, [defaultLanguage])

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  useEffect(() => {
    axios.get('/api/trtc/config')
      .then((res) => setTrtcConfigured(!!res.data?.configured))
      .catch(() => setTrtcConfigured(false))
  }, [])

  useEffect(() => {
    if (callState?.status !== 'ended') return undefined
    const timer = window.setTimeout(() => {
      endCall()
    }, 2000)
    return () => window.clearTimeout(timer)
  }, [callState?.status, endCall])

  useEffect(() => {
    if (!Object.keys(msgTranslations).length) return
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
  }, [msgTranslations])

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape' && quoteDraft) clearQuoteDraft()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [quoteDraft])

  useEffect(() => {
    const onDocClick = (e) => {
      if (showNewMenu && !e.target.closest('.rs-chat-new-wrap')) setShowNewMenu(false)
      if (showEmoji && inputAreaRef.current && !inputAreaRef.current.contains(e.target)) setShowEmoji(false)
      if (showFilePanel && inputAreaRef.current && !inputAreaRef.current.contains(e.target)) setShowFilePanel(false)
      if (ctxMenu && !e.target.closest('.rs-chat-ctx-menu') && !e.target.closest('.rs-chat-msg-bubble')) {
        setCtxMenu(null)
      }
    }
    document.addEventListener('click', onDocClick)
    return () => document.removeEventListener('click', onDocClick)
  }, [showNewMenu, showEmoji, showFilePanel, ctxMenu])

  const loadUserFiles = useCallback(async () => {
    setFilesLoading(true)
    try {
      const res = await axios.get('/api/files')
      setUserFiles(res.data || [])
    } catch {
      toast.error('Failed to load files')
      setUserFiles([])
    } finally {
      setFilesLoading(false)
    }
  }, [])

  const openFilePanel = () => {
    setShowEmoji(false)
    setShowFilePanel((v) => {
      if (!v) loadUserFiles()
      return !v
    })
  }

  const openLocalFilePicker = () => {
    if (!selectedContact) return
    setShowEmoji(false)
    if (!canChat(selectedContact)) {
      setShowPaywall(true)
      toast.error('Upgrade your membership to send files.')
      return
    }
    localFileInputRef.current?.click()
  }

  const revokePendingPreview = (item) => {
    if (item?.previewUrl) window.URL.revokeObjectURL(item.previewUrl)
  }

  const clearPendingAttachments = () => {
    setPendingAttachments((prev) => {
      prev.forEach(revokePendingPreview)
      return []
    })
  }

  const removePendingAttachment = (id) => {
    setPendingAttachments((prev) => {
      const item = prev.find((p) => p.id === id)
      revokePendingPreview(item)
      return prev.filter((p) => p.id !== id)
    })
  }

  const uploadChatFile = async (file, filename) => {
    const form = new FormData()
    form.append('file', file, filename || file.name)
    const res = await axios.post('/api/files/upload', form)
    return res.data
  }

  const queuePendingImage = async (blob, filename) => {
    const localId = `pending-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
    const previewUrl = window.URL.createObjectURL(blob)
    setPendingAttachments((prev) => [
      ...prev,
      { id: localId, filename, previewUrl, uploading: true, uploaded: null, error: null },
    ])
    try {
      const uploaded = await uploadChatFile(blob, filename)
      setPendingAttachments((prev) =>
        prev.map((p) => (p.id === localId ? { ...p, uploading: false, uploaded } : p))
      )
    } catch (err) {
      setPendingAttachments((prev) =>
        prev.map((p) =>
          p.id === localId
            ? { ...p, uploading: false, error: err.response?.data?.message || 'Upload failed' }
            : p
        )
      )
    }
  }

  const handleLocalFileSelected = async (e) => {
    const picked = e.target.files?.[0]
    e.target.value = ''
    if (!picked || !selectedContact) return
    if (!canChat(selectedContact)) {
      setShowPaywall(true)
      return
    }
    if (isImageFileName(picked.name)) {
      await queuePendingImage(picked, picked.name)
      return
    }
    setUploadingLocalFile(true)
    try {
      const uploaded = await uploadChatFile(picked, picked.name)
      await sendFileMessage(uploaded)
      if (showFilePanel) loadUserFiles()
    } catch (err) {
      toast.error(err.response?.data?.message || 'Upload failed')
    } finally {
      setUploadingLocalFile(false)
    }
  }

  const handlePaste = async (e) => {
    if (!selectedContact || !canChat(selectedContact)) return
    const items = e.clipboardData?.items
    if (!items?.length) return
    const imageItems = Array.from(items).filter((item) => item.type.startsWith('image/'))
    if (!imageItems.length) return
    e.preventDefault()
    for (const item of imageItems) {
      const blob = item.getAsFile()
      if (!blob) continue
      await queuePendingImage(blob, buildPasteImageFilename(blob.type))
    }
  }

  useEffect(() => {
    return () => {
      pendingAttachmentsRef.current.forEach((item) => {
        if (item?.previewUrl) window.URL.revokeObjectURL(item.previewUrl)
      })
    }
  }, [])

  const sendChatPayload = async (
    { content, messageType = 'text', fileUrl, replyToId },
    targetContact = selectedContact
  ) => {
    const payload = content?.trim()
    if (!payload || !targetContact) return false
    const lastPreview = messageType === 'file' ? `File: ${payload}` : payload
    const body = {
      content: payload,
      messageType,
      ...(fileUrl ? { fileUrl } : {}),
      ...(replyToId ? { replyToId } : {}),
    }

    if (targetContact.isGroup) {
      if (!canChat(targetContact)) {
        setShowPaywall(true)
        toast.error('Upgrade your membership to send group messages.')
        return false
      }
      try {
        if (isConnected()) {
          await sendViaSocket({ conversationId: targetContact.id, ...body })
        } else {
          const res = await axios.post(`/api/chat/groups/${targetContact.id}/send`, body)
          const msg = mapMessage(res.data, user?.id)
          if (targetContact.id === selectedContact?.id) {
            setMessages((prev) => (prev.some((m) => m.id === msg.id) ? prev : [...prev, msg]))
          }
        }
        setContacts((prev) =>
          prev.map((c) =>
            c.id === targetContact.id ? { ...c, lastMessage: lastPreview, lastTime: 'Just now' } : c
          )
        )
        return true
      } catch (err) {
        const data = err.response?.data || {}
        if (data?.code === 'PAYWALL' || err.response?.status === 402) {
          setShowPaywall(true)
          toast.error('Upgrade your membership to send group messages.')
          return false
        }
        toast.error(data?.message || 'Failed to send message')
        return false
      }
    }

    if (!canChat(targetContact)) {
      setShowPaywall(true)
      toast.error('Upgrade to message GP/LP/Startup members. You can still contact Platform Admin and service providers for free.')
      return false
    }
    try {
      if (isConnected()) {
        await sendViaSocket({ receiverId: targetContact.id, ...body })
      } else {
        const res = await axios.post('/api/chat/send', { receiverId: targetContact.id, ...body })
        const msg = mapMessage(res.data, user?.id)
        if (targetContact.id === selectedContact?.id) {
          setMessages((prev) => (prev.some((m) => m.id === msg.id) ? prev : [...prev, msg]))
        }
      }
      setContacts((prev) =>
        prev.map((c) =>
          c.id === targetContact.id ? { ...c, lastMessage: lastPreview, lastTime: 'Just now' } : c
        )
      )
      return true
    } catch (err) {
      const data = err.response?.data || {}
      if (data?.code === 'PAYWALL' || err.response?.status === 402) {
        setShowPaywall(true)
        toast.error('Upgrade to message GP/LP/Startup members.')
        return false
      }
      if (data?.code === 'CONTACT_QUOTA_EXCEEDED' || err.response?.status === 403) {
        toast.error(data?.message || 'Monthly new contact limit reached')
        return false
      }
      if (data?.code === 'CHAT_BLOCKED' || (err.response?.status === 403 && data?.message?.includes('block'))) {
        toast.error(data?.message || 'Cannot send message')
        return false
      }
      toast.error(data?.message || 'Failed to send message')
      return false
    }
  }

  const sendText = async (text, targetContact = selectedContact, replyToId = null) => {
    return sendChatPayload({ content: text, replyToId }, targetContact)
  }

  const clearQuoteDraft = () => setQuoteDraft(null)

  const scrollToQuotedMessage = (messageId) => {
    if (!messageId) return
    const el = document.querySelector(`[data-msg-id="${messageId}"]`)
    if (!el) {
      toast.info('Original message is not in the current view')
      return
    }
    el.scrollIntoView({ behavior: 'smooth', block: 'center' })
    el.classList.add('rs-chat-msg-highlight')
    window.setTimeout(() => el.classList.remove('rs-chat-msg-highlight'), 1600)
  }

  const handleSend = async () => {
    if (!selectedContact) return
    if (!canChat(selectedContact)) {
      setShowPaywall(true)
      return
    }
    const text = inputMessage.trim()
    const readyAttachments = pendingAttachments.filter((p) => p.uploaded && !p.error)
    const hasUploading = pendingAttachments.some((p) => p.uploading)
    if (!text && !readyAttachments.length) return
    if (hasUploading) {
      toast.error('Please wait for the image upload to finish')
      return
    }

    setInputMessage('')
    setShowEmoji(false)
    setShowFilePanel(false)
    if (inputRef.current) inputRef.current.style.height = 'auto'

    const attachmentsToSend = [...readyAttachments]
    const replyToId = quoteDraft?.id || null
    clearPendingAttachments()
    clearQuoteDraft()

    if (text) await sendText(text, selectedContact, replyToId)
    for (const att of attachmentsToSend) {
      await sendFileMessage(att.uploaded, { silent: true })
    }
    if (attachmentsToSend.length) {
      toast.success(attachmentsToSend.length > 1 ? 'Images sent' : 'Image sent')
    }
  }

  const sendFileMessage = async (file, { silent = false } = {}) => {
    const name = file.original_name || file.name
    const fileId = file.id
    if (!fileId) {
      toast.error('Invalid file')
      return
    }
    let fileUrl = `/api/files/download/${fileId}`
    const fromDownloadUrl = String(file.downloadUrl || file.url || '').match(/\/api\/files\/download\/[^/?#]+/i)
    if (fromDownloadUrl) fileUrl = fromDownloadUrl[0]
    setShowFilePanel(false)
    const ok = await sendChatPayload({
      content: name,
      messageType: 'file',
      fileUrl,
    })
    if (ok && !silent) toast.success('File sent')
  }

  const handleChatFileDownload = async (msg, e) => {
    e?.stopPropagation()
    e?.preventDefault()
    if (msg.legacyFile) {
      toast.error('This legacy file cannot be downloaded. Ask the sender to resend it.')
      return
    }
    const fileId = msg.fileId || extractFileId(msg.fileUrl)
    const url = msg.id
      ? `/api/chat/messages/${msg.id}/file`
      : fileId
        ? `/api/files/download/${fileId}`
        : null
    if (!url) {
      toast.error('File unavailable')
      return
    }
    try {
      const res = await axios.get(url, { responseType: 'blob' })
      const contentType = String(res.headers['content-type'] || '')
      if (contentType.includes('application/json')) {
        const json = JSON.parse(await res.data.text())
        toast.error(json.message || 'Download failed')
        return
      }
      const blobUrl = window.URL.createObjectURL(res.data)
      const a = document.createElement('a')
      a.href = blobUrl
      a.download = msg.fileName || msg.text || 'download'
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(blobUrl)
      toast.success(`Downloaded ${msg.fileName || msg.text}`)
    } catch (err) {
      toast.error(await parseDownloadError(err))
    }
  }

  const handleMessageTranslate = (msg) => {
    if (!msg?.text || recalledMsgIds.includes(msg.id)) return
    if (msg.sender !== 'them' || msg.messageType === 'file') return
    const key = msgTranslationKey(msg.id)
    if (msgTranslations[key]) {
      setMsgTranslations((prev) => {
        const next = { ...prev }
        delete next[key]
        return next
      })
      return
    }
    translateIncomingMessage(msg)
  }

  const translateLatestIncoming = () => {
    const latest = [...messages].reverse().find(
      (m) => m.sender === 'them' && m.text && m.messageType !== 'file' && !recalledMsgIds.includes(m.id)
    )
    if (!latest) {
      toast('No incoming messages to translate')
      return
    }
    handleMessageTranslate(latest)
  }

  const closeScreenshotModal = () => {
    setShowScreenshot(false)
    setScreenshotPreview(null)
    setScreenshotCapturing(false)
    setScreenshotSaving(false)
  }

  const captureChatScreenshot = async () => {
    if (!chatMessagesRef.current) {
      throw new Error('Chat messages not ready')
    }
    const html2canvas = (await import('html2canvas')).default
    const canvas = await html2canvas(chatMessagesRef.current, {
      backgroundColor: '#0f172a',
      scale: Math.min(window.devicePixelRatio || 1, 2),
      useCORS: true,
      logging: false,
      scrollY: -window.scrollY,
    })
    return canvas.toDataURL('image/png')
  }

  const openScreenshotModal = async () => {
    if (!selectedContact) {
      toast.error('Select a conversation first')
      return
    }
    if (!filteredMessages.some((m) => m.sender !== 'system')) {
      toast.error('No messages to capture')
      return
    }
    setShowScreenshot(true)
    setScreenshotPreview(null)
    setScreenshotCapturing(true)
    try {
      const dataUrl = await captureChatScreenshot()
      setScreenshotPreview(dataUrl)
    } catch {
      toast.error('Could not capture chat area')
      closeScreenshotModal()
    } finally {
      setScreenshotCapturing(false)
    }
  }

  const saveScreenshotToFiles = async () => {
    if (!screenshotPreview) return
    setScreenshotSaving(true)
    try {
      const blob = await fetch(screenshotPreview).then((r) => r.blob())
      const contactSlug = (convDisplayName(selectedContact) || 'chat')
        .replace(/[^\w.-]+/g, '_')
        .slice(0, 40)
      const filename = `screenshot_chat_${contactSlug}_${Date.now()}.png`
      const form = new FormData()
      form.append('file', blob, filename)
      await axios.post('/api/files/upload', form)
      toast.success('Saved to Files > Screenshots')
      closeScreenshotModal()
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save screenshot')
    } finally {
      setScreenshotSaving(false)
    }
  }

  const openMsgMenu = (e, msg) => {
    e.stopPropagation()
    if (recalledMsgIds.includes(msg.id)) return
    setCtxMenu({ x: e.clientX, y: e.clientY, msg })
  }

  const handleCtxAction = async (action) => {
    const msg = ctxMenu?.msg
    if (!msg) return
    const text = recalledMsgIds.includes(msg.id) ? '' : (msg.text || '')
    setCtxMenu(null)
    switch (action) {
      case 'copy':
        try {
          await navigator.clipboard.writeText(text)
          toast.success('Copied')
        } catch {
          toast.error('Copy failed')
        }
        break
      case 'expand':
        setExpandText(text)
        break
      case 'forward':
        setForwardDraft({ text, from: selectedContact })
        setShowForwardModal(true)
        break
      case 'favorite': {
        const favorites = JSON.parse(localStorage.getItem('chatFavorites') || '[]')
        favorites.push({
          text,
          chat: selectedContact ? convDisplayName(selectedContact) : '',
          at: new Date().toISOString(),
        })
        localStorage.setItem('chatFavorites', JSON.stringify(favorites.slice(-100)))
        toast.success('Added to favorites')
        break
      }
      case 'quote':
        setQuoteDraft(buildQuoteFromMessage(msg, { selectedContact }))
        setShowEmoji(false)
        setShowFilePanel(false)
        inputRef.current?.focus()
        break
      case 'delete':
        setHiddenMsgIds((prev) => [...prev, msg.id])
        toast.success('Message hidden')
        break
      case 'recall':
        setRecalledMsgIds((prev) => [...prev, msg.id])
        toast.success('Message recalled')
        break
      default:
        break
    }
  }

  const createGroup = async () => {
    const minMembers = user?.role === 'Admin' && matchDeal ? 1 : 2
    if (!groupName.trim() || groupPickIds.length < minMembers) {
      toast.error(
        minMembers === 1
          ? 'Enter a group name and select at least 1 member'
          : 'Enter a group name and select at least 2 members'
      )
      return
    }
    if (1 + groupPickIds.length > MAX_GROUP_MEMBERS) {
      toast.error(`Group chats are limited to ${MAX_GROUP_MEMBERS} members (including you)`)
      return
    }
    setCreatingGroup(true)
    try {
      const res = await axios.post('/api/chat/groups', {
        title: groupName.trim(),
        memberIds: groupPickIds,
        dealId: matchDeal?.id || searchParams.get('deal') || null,
      })
      const groupContact = res.data.group
      setContacts((prev) => [groupContact, ...prev.filter((c) => c.id !== groupContact.id)])
      setShowGroupModal(false)
      setGroupName('')
      setGroupPickIds([])
      setGroupMemberSearch('')
      setMatchDeal(null)
      toast.success(
        res.data.existing
          ? `Opened existing group "${groupContact.name}"`
          : `Group "${groupContact.name}" created`
      )
      joinConversation(groupContact.id)
      loadChatHistory(groupContact)
    } catch (err) {
      const data = err.response?.data || {}
      if (data?.code === 'PAYWALL' || err.response?.status === 402) {
        setShowPaywall(true)
        toast.error('Upgrade your membership to create group chats.')
        return
      }
      toast.error(data?.message || 'Failed to create group')
    } finally {
      setCreatingGroup(false)
    }
  }

  const addMembersToGroup = async () => {
    if (!selectedContact?.isGroup || !groupPickIds.length) {
      toast.error('Select at least one member to add')
      return
    }
    if (groupMembers.length + groupPickIds.length > MAX_GROUP_MEMBERS) {
      toast.error(`Groups are limited to ${MAX_GROUP_MEMBERS} members. You can add ${Math.max(0, MAX_GROUP_MEMBERS - groupMembers.length)} more.`)
      return
    }
    setCreatingGroup(true)
    try {
      const res = await axios.post(`/api/chat/groups/${selectedContact.id}/members`, {
        memberIds: groupPickIds,
      })
      const count = res.data?.memberIds?.length || groupMembers.length + groupPickIds.length
      setContacts((prev) => prev.map((c) => (
        c.id === selectedContact.id
          ? { ...c, memberCount: count, company: `${count} member${count === 1 ? '' : 's'}` }
          : c
      )))
      setSelectedContact((prev) => (prev?.id === selectedContact.id
        ? { ...prev, memberCount: count, company: `${count} member${count === 1 ? '' : 's'}` }
        : prev))
      await loadGroupMembers(selectedContact.id)
      const histRes = await axios.get(`/api/chat/groups/${selectedContact.id}/history`)
      setMessages(histRes.data.map((row) => mapMessage(row, user?.id)))
      closeGroupModal()
      toast.success(`Added ${res.data?.addedIds?.length || groupPickIds.length} member(s)`)
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to add members')
    } finally {
      setCreatingGroup(false)
    }
  }

  const handleLeaveGroup = async () => {
    if (!selectedContact?.isGroup) return
    const name = convDisplayName(selectedContact)
    const ok = await confirm({
      title: 'Leave group?',
      message: `Leave "${name}"? You will no longer receive messages from this group.`,
      confirmText: 'Leave',
      variant: 'danger',
    })
    if (!ok) return
    setLeavingGroup(true)
    try {
      await axios.post(`/api/chat/groups/${selectedContact.id}/leave`)
      setContacts((prev) => prev.filter((c) => c.id !== selectedContact.id))
      setSelectedContact(null)
      setMessages([])
      setGroupMembers([])
      setShowGroupMembersPanel(false)
      toast.success('Left group')
      window.dispatchEvent(new Event('rs-chat-unread-changed'))
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to leave group')
    } finally {
      setLeavingGroup(false)
    }
  }

  const handleToggleBlock = async () => {
    if (!selectedContact || selectedContact.isGroup) return
    const peerName = convDisplayName(selectedContact)
    if (peerBlockStatus.blockedByMe) {
      const ok = await confirm({
        title: 'Unblock user?',
        message: `Unblock ${peerName}? You will be able to chat and call each other again.`,
        confirmText: 'Unblock',
      })
      if (!ok) return
      setBlockActionLoading(true)
      try {
        const res = await axios.delete(`/api/chat/block/${selectedContact.id}`)
        setPeerBlockStatus(res.data)
        toast.success('User unblocked')
      } catch (err) {
        toast.error(err.response?.data?.message || 'Failed to unblock user')
      } finally {
        setBlockActionLoading(false)
      }
      return
    }
    const ok = await confirm({
      title: 'Block user?',
      message: `Block ${peerName}? They will not be able to send you messages or calls.`,
      confirmText: 'Block',
      variant: 'danger',
    })
    if (!ok) return
    setBlockActionLoading(true)
    try {
      const res = await axios.post(`/api/chat/block/${selectedContact.id}`)
      setPeerBlockStatus(res.data)
      toast.success('User blocked')
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to block user')
    } finally {
      setBlockActionLoading(false)
    }
  }

  const handleDissolveGroup = async () => {
    if (!selectedContact?.isGroup || !isGroupOwner) return
    const name = convDisplayName(selectedContact)
    const ok = await confirm({
      title: 'Dissolve group?',
      message: `Dissolve group "${name}"? All members will be removed and chat history will be deleted.`,
      confirmText: 'Dissolve',
      variant: 'danger',
    })
    if (!ok) {
      return
    }
    setDissolvingGroup(true)
    try {
      await axios.delete(`/api/chat/groups/${selectedContact.id}`)
      setContacts((prev) => prev.filter((c) => c.id !== selectedContact.id))
      setSelectedContact(null)
      setMessages([])
      setGroupMembers([])
      setShowGroupMembersPanel(false)
      toast.success('Group dissolved')
      window.dispatchEvent(new Event('rs-chat-unread-changed'))
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to dissolve group')
    } finally {
      setDissolvingGroup(false)
    }
  }

  const handleForwardTo = async (contact) => {
    if (!forwardDraft?.text || contact.isGroup) return
    const body = `Forwarded message:\n${forwardDraft.text}`
    const ok = await sendText(body, contact)
    if (ok) {
      toast.success(`Forwarded to ${convDisplayName(contact)}`)
      setShowForwardModal(false)
      setForwardDraft(null)
    }
  }

  const startCall = async (mode) => {
    if (!selectedContact || selectedContact.isGroup) return
    if (!canChat(selectedContact)) {
      setShowPaywall(true)
      return
    }
    if (callState || incomingCall) {
      toast.error('Already in a call')
      return
    }
    setStartingCall(true)
    try {
      if (trtcConfigured === false) {
        toast.error('TRTC not configured. Set TRTC_SDK_APP_ID and TRTC_SECRET_KEY in backend/.env')
        return
      }
      const res = await axios.post('/api/trtc/call/start', {
        calleeId: selectedContact.id,
        mode,
      })
      const peerName = convDisplayName(selectedContact)
      await joinCall({
        sdkAppId: res.data.sdkAppId,
        userId: res.data.userId,
        userSig: res.data.userSig,
        strRoomId: res.data.strRoomId,
        mode: res.data.mode,
        peerName,
        callId: res.data.callId,
        isIncoming: false,
      })
      await sendText(`${mode === 'video' ? 'Video' : 'Voice'} call invitation — please accept the incoming call`)
      toast.success(`${mode === 'video' ? 'Video' : 'Voice'} call started`)
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to start call')
    } finally {
      setStartingCall(false)
    }
  }

  const acceptIncomingCall = async () => {
    if (!incomingCall) return
    setAcceptingCall(true)
    const callMeta = incomingCall
    setIncomingCall(null)
    try {
      const res = await axios.post('/api/trtc/call/join', { callId: callMeta.callId })
      const peerName = callMeta.callerName || 'Contact'
      await joinCall({
        sdkAppId: res.data.sdkAppId,
        userId: res.data.userId,
        userSig: res.data.userSig,
        strRoomId: res.data.strRoomId,
        mode: res.data.mode,
        peerName,
        callId: res.data.callId,
        isIncoming: true,
      })
    } catch (err) {
      const peerEnded = /leave room|connection aborted/i.test(String(err?.message || ''))
      if (peerEnded) {
        await endCall()
      } else {
        const msg = err.response?.data?.message || err.message || 'Failed to join call'
        toast.error(msg)
        try {
          await axios.post('/api/trtc/call/end', { callId: callMeta.callId })
        } catch {
          /* ignore */
        }
        await endCall()
      }
    } finally {
      setAcceptingCall(false)
    }
  }

  const declineIncomingCall = async () => {
    if (!incomingCall) return
    try {
      await axios.post('/api/trtc/call/end', { callId: incomingCall.callId })
    } catch {
      /* ignore */
    }
    setIncomingCall(null)
  }

  const hangUpCall = async () => {
    const callId = callState?.callId
    try {
      if (callId) await axios.post('/api/trtc/call/end', { callId })
    } catch {
      /* ignore */
    }
    await endCall()
    setIncomingCall(null)
  }

  const loadGroupMembers = async (groupId) => {
    setGroupMembersLoading(true)
    try {
      const res = await axios.get(`/api/chat/groups/${groupId}/members`)
      const data = res.data
      setGroupMembers(Array.isArray(data) ? data : (data.members || []))
    } catch {
      setGroupMembers([])
      toast.error('Failed to load group members')
    } finally {
      setGroupMembersLoading(false)
    }
  }
  loadGroupMembersRef.current = loadGroupMembers

  const openMemberPrivateChat = (member) => {
    if (!member?.id || member.id === user?.id) return
    const existing = contacts.find((c) => c.id === member.id && !c.isGroup)
    if (existing) {
      loadChatHistory(existing)
      return
    }
    const contact = {
      id: member.id,
      name: member.name,
      system_name: member.system_name,
      real_name: member.real_name,
      role: member.role,
      company: member.company,
      lastMessage: 'No messages yet',
      lastTime: '',
      unread: 0,
      online: 'offline',
    }
    setContacts((prev) => (prev.some((c) => c.id === contact.id) ? prev : [contact, ...prev]))
    loadChatHistory(contact)
  }

  const loadChatHistory = async (contact) => {
    setSelectedContact(contact)
    clearPendingAttachments()
    clearQuoteDraft()
    setMiddleMode('chat')
    setDetailContact(null)
    setHiddenMsgIds([])
    setRecalledMsgIds([])
    setMsgTranslations({})
    setTranslatingMsgId(null)
    translatingIdsRef.current.clear()
    setCtxMenu(null)
    if (contact.isGroup) {
      setShowGroupMembersPanel(true)
      loadGroupMembers(contact.id)
      joinConversation(contact.id)
      try {
        const res = await axios.get(`/api/chat/groups/${contact.id}/history`)
        const mapped = res.data.map((row) => mapMessage(row, user?.id))
        setMessages(mapped)
        await applyCachedTranslations(mapped)
        setContacts((prev) => prev.map((c) => (c.id === contact.id ? { ...c, unread: 0 } : c)))
        window.dispatchEvent(new Event('rs-chat-unread-changed'))
        window.dispatchEvent(new Event('rs-notifications-changed'))
      } catch {
        toast.error('Failed to load messages')
        setMessages([])
      }
      return
    }
    setGroupMembers([])
    setShowGroupMembersPanel(false)
    try {
      const res = await axios.get(`/api/chat/history/${contact.id}`)
      const mapped = res.data.map((row) => mapMessage(row, user?.id))
      setMessages(mapped)
      await applyCachedTranslations(mapped)
      setContacts((prev) => prev.map((c) => (c.id === contact.id ? { ...c, unread: 0 } : c)))
      window.dispatchEvent(new Event('rs-chat-unread-changed'))
      window.dispatchEvent(new Event('rs-notifications-changed'))
    } catch {
      toast.error('Failed to load messages')
      setMessages([])
    }
  }

  useEffect(() => {
    const contactId = searchParams.get('contact')
    if (!contactId) return
    const found = contacts.find((c) => String(c.id) === String(contactId))
    if (found) {
      if (selectedContactRef.current?.id !== found.id) loadChatHistory(found)
      return
    }
    let cancelled = false
    axios.get(`/api/users/${contactId}`).then((res) => {
      if (cancelled || !res.data?.id) return
      const u = res.data
      const contact = {
        id: u.id,
        name: u.system_name,
        role: u.role,
        company: u.company_short_name || u.company_full_name,
        isPaid: !!u.is_paid,
        lastMessage: 'No messages yet',
        lastTime: '',
        unread: 0,
        online: 'offline',
      }
      setContacts((prev) => (prev.some((c) => c.id === contact.id) ? prev : [contact, ...prev]))
      if (selectedContactRef.current?.id !== contact.id) loadChatHistory(contact)
    }).catch(() => toast.error('Contact not found or failed to load'))
    return () => { cancelled = true }
  }, [searchParams, contacts, user?.id])

  useEffect(() => {
    const dealId = searchParams.get('deal')
    const shouldCreateGroup = searchParams.get('createGroup') === '1'
    if (!dealId || user?.role !== 'Admin' || !shouldCreateGroup) return
    if (dealMatchHandledRef.current === dealId) return

    let cancelled = false
    ;(async () => {
      try {
        const existingRes = await axios.get(`/api/chat/groups/by-deal/${dealId}`)
        if (cancelled) return
        if (existingRes.data?.group) {
          dealMatchHandledRef.current = dealId
          const g = existingRes.data.group
          setContacts((prev) => (prev.some((c) => c.id === g.id) ? prev : [g, ...prev]))
          loadChatHistory(g)
          toast.success('Opened existing matchmaking group')
          return
        }

        const dealRes = await axios.get(`/api/deals/${dealId}`)
        if (cancelled) return
        const deal = dealRes.data
        const requesterId = searchParams.get('contact')
        const picks = []
        if (deal.gp_id) picks.push(String(deal.gp_id))
        if (requesterId && requesterId !== deal.gp_id) picks.push(String(requesterId))

        dealMatchHandledRef.current = dealId
        setMatchDeal(deal)
        setGroupName(`${deal.title} · Matchmaking`)
        setGroupPickIds([...new Set(picks)])
        setShowGroupModal(true)
        toast('Create a matchmaking group for GP and LP')
      } catch (err) {
        toast.error(err.response?.data?.message || 'Failed to load deal for matchmaking')
      }
    })()

    return () => { cancelled = true }
  }, [searchParams, user?.id, user?.role])

  const filteredContacts = useMemo(() => {
    return contacts.filter((c) => contactMatchesSearch(c, searchQuery))
  }, [contacts, searchQuery])

  const platformAdminContact = useMemo(
    () => filteredContacts.find((c) => c.isPlatformAdmin) || null,
    [filteredContacts]
  )

  const { pinned, regular } = useMemo(() => {
    const pinSet = new Set(pinnedIds.map(String))
    const p = []
    const r = []
    filteredContacts.forEach((c) => {
      if (pinSet.has(String(c.id))) p.push(c)
      else r.push(c)
    })
    return { pinned: p, regular: r }
  }, [filteredContacts, pinnedIds])

  const serviceProviders = useMemo(
    () => filteredContacts.filter((c) => c.role === 'ServiceProvider'),
    [filteredContacts]
  )

  const groupPickCandidates = useMemo(() => {
    const inGroupIds = groupModalMode === 'add'
      ? new Set(groupMembers.map((m) => String(m.id)))
      : new Set()
    return contacts.filter(
      (c) => !c.isGroup
        && !c.isPlatformAdmin
        && !inGroupIds.has(String(c.id))
        && contactMatchesSearch(c, groupMemberSearch)
    )
  }, [contacts, groupMemberSearch, groupModalMode, groupMembers])

  const groupPickLimit = useMemo(() => {
    if (groupModalMode === 'add') {
      return Math.max(0, MAX_GROUP_MEMBERS - groupMembers.length)
    }
    return MAX_GROUP_MEMBERS - 1
  }, [groupModalMode, groupMembers.length])

  const toggleGroupPick = (id, checked) => {
    setGroupPickIds((prev) => {
      if (checked) {
        if (prev.includes(id)) return prev
        if (prev.length >= groupPickLimit) {
          toast.error(
            groupModalMode === 'add'
              ? `You can add up to ${groupPickLimit} more members (group limit: ${MAX_GROUP_MEMBERS})`
              : `Group chats are limited to ${MAX_GROUP_MEMBERS} members (including you)`
          )
          return prev
        }
        return [...prev, id]
      }
      return prev.filter((x) => x !== id)
    })
  }

  const isGroupOwner = useMemo(() => {
    if (!selectedContact?.isGroup || !user?.id) return false
    if (String(selectedContact.createdBy) === String(user.id)) return true
    const me = groupMembers.find((m) => String(m.id) === String(user.id))
    return me?.memberRole === 'owner'
  }, [selectedContact, user?.id, groupMembers])

  const closeGroupModal = () => {
    setShowGroupModal(false)
    setMatchDeal(null)
    setGroupMemberSearch('')
    setGroupPickIds([])
    setGroupModalMode('create')
  }

  const openAddMembersModal = () => {
    if (!selectedContact?.isGroup || !isGroupOwner) return
    const remaining = MAX_GROUP_MEMBERS - groupMembers.length
    if (remaining <= 0) {
      toast.error(`This group is full (max ${MAX_GROUP_MEMBERS} members)`)
      return
    }
    setGroupModalMode('add')
    setGroupPickIds([])
    setGroupMemberSearch('')
    setShowGroupModal(true)
  }

  const alphaContacts = useMemo(() => {
    const list = filteredContacts
      .filter((c) => c.role !== 'ServiceProvider' && c.role !== 'Admin' && !c.isPlatformAdmin)
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
    const groups = {}
    list.forEach((c) => {
      const letter = (c.name || '?').charAt(0).toUpperCase()
      if (!groups[letter]) groups[letter] = []
      groups[letter].push(c)
    })
    return groups
  }, [filteredContacts])

  const filteredMessages = useMemo(() => {
    const list = messages.filter((m) => !hiddenMsgIds.includes(m.id))
    if (!chatSearchQuery.trim()) return list
    const q = chatSearchQuery.toLowerCase()
    return list.filter((m) => m.text?.toLowerCase().includes(q))
  }, [messages, chatSearchQuery, hiddenMsgIds])

  const togglePin = () => {
    if (!selectedContact) return
    const id = String(selectedContact.id)
    setPinnedIds((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
      localStorage.setItem('pinnedChats', JSON.stringify(next))
      return next
    })
  }

  const toggleChatMute = () => {
    if (!selectedContact) return
    const id = String(selectedContact.id)
    setMutedIds((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
      localStorage.setItem('mutedChats', JSON.stringify(next))
      toast.success(next.includes(id) ? 'Notifications muted' : 'Notifications on')
      return next
    })
  }

  const isPinned = selectedContact && pinnedIds.includes(String(selectedContact.id))
  const isMuted = selectedContact && mutedIds.includes(String(selectedContact.id))

  const toggleGroup = (key) => {
    setCollapsedGroups((p) => ({ ...p, [key]: !p[key] }))
  }

  const insertEmoji = (emoji) => {
    setInputMessage((prev) => prev + emoji)
    setShowEmoji(false)
    inputRef.current?.focus()
  }

  const roleBadgeClass = (role) => {
    if (role === 'Admin') return 'admin'
    if (role === 'GP') return 'gp'
    if (role === 'LP') return 'lp'
    if (role === 'Startup') return 'startup'
    if (role === 'ServiceProvider') return 'service'
    return 'gp'
  }

  const renderContactRow = (contact) => (
    <button
      key={contact.id}
      type="button"
      className={`rs-chat-contact-item${!canChat(contact) ? ' greyed' : ''}${contact.isPlatformAdmin ? ' platform-admin' : ''}`}
      onClick={() => setDetailContact(contact)}
    >
      <div className="rs-chat-conv-avatar-wrap">
        <ChatAvatar contact={contact} />
      </div>
        <div className="rs-chat-contact-info">
          <div className="rs-chat-conv-name">{convDisplayName(contact)}</div>
          <div className="rs-chat-contact-desc">
          {[contact.role, contact.company].filter(Boolean).join(' · ') || '—'}
        </div>
      </div>
    </button>
  )

  const renderConvRow = (contact) => (
    <button
      key={contact.id}
      type="button"
      className={`rs-chat-conv-row${selectedContact?.id === contact.id ? ' active' : ''}`}
      onClick={() => loadChatHistory(contact)}
    >
      <div className="rs-chat-conv-avatar-wrap">
        <ChatAvatar contact={contact} />
        {contact.unread > 0 && (
          <span className="rs-chat-unread-badge">{contact.unread > 99 ? '99+' : contact.unread}</span>
        )}
      </div>
      <div className="rs-chat-conv-info">
        <div className="rs-chat-conv-row-top">
          <span className="rs-chat-conv-name">{convDisplayName(contact)}</span>
          <span className="rs-chat-conv-time">{formatConvTime(contact.lastTime)}</span>
        </div>
        <div className="rs-chat-conv-preview">
          {contact.lastMessageMasked && <span style={{ color: '#f59e0b' }}>[masked] </span>}
          {contact.lastMessage || 'No messages yet'}
        </div>
      </div>
    </button>
  )

  const readyPendingCount = pendingAttachments.filter((p) => p.uploaded && !p.error).length
  const hasPendingUploading = pendingAttachments.some((p) => p.uploading)
  const canSendMessage = Boolean(
    selectedContact
    && canChat(selectedContact)
    && !peerBlockStatus.blocked
    && (inputMessage.trim() || readyPendingCount > 0)
    && !hasPendingUploading
  )

  return (
    <PageShell embedded={embedded} fullBleed>
      <UpgradeModal
        open={showPaywall}
        onClose={() => setShowPaywall(false)}
        onUpgrade={() => {
          setShowPaywall(false)
          navigate('/payment')
        }}
        message="Free users cannot message GP/LP/Startup members. Upgrade to unlock private chat; contacting Platform Admin and service providers remains free."
      />
      <div className="rs-chat-root">
        {/* Icon sidebar */}
        <aside className="rs-chat-icon-bar">
          <ChatAvatar contact={{ id: user?.id, name: user?.system_name, role: user?.role }} size={40} className="rs-chat-user-avatar" alt={user?.system_name || 'Me'} />
          <button
            type="button"
            className={`rs-chat-icon-btn${middleMode === 'chat' ? ' active' : ''}`}
            title="Chat List"
            onClick={() => { setMiddleMode('chat'); setDetailContact(null) }}
          >
            <RsIcon name="chat" size="md" />
          </button>
          <span className="rs-chat-icon-label">Chat</span>
          <button
            type="button"
            className={`rs-chat-icon-btn${middleMode === 'contacts' ? ' active' : ''}`}
            title="Contacts"
            onClick={() => { setMiddleMode('contacts'); setDetailContact(null) }}
          >
            <RsIcon name="users" size="md" />
          </button>
          <span className="rs-chat-icon-label">Contacts</span>
        </aside>

        {/* Middle panel */}
        <div className="rs-chat-middle">
          {!detailContact ? (
            <>
              <div className="rs-chat-mp-header">
                <div className="rs-chat-new-wrap">
                  <button type="button" className="rs-chat-new-btn" onClick={() => setShowNewMenu((v) => !v)}>＋</button>
                  {showNewMenu && (
                    <div className="rs-chat-new-dropdown show">
                      <button type="button" className="rs-chat-new-item" onClick={() => { setShowNewMenu(false); setMiddleMode('contacts') }}>
                        <IconLabel name="chat" size="xs">New Chat</IconLabel>
                      </button>
                      <button type="button" className="rs-chat-new-item" onClick={() => { setShowNewMenu(false); setGroupModalMode('create'); setGroupPickIds([]); setShowGroupModal(true) }}>
                        <IconLabel name="userPlus" size="xs">Create Group</IconLabel>
                      </button>
                    </div>
                  )}
                </div>
                <div className="rs-chat-mp-header-status">
                  {onlineCountEnabled && (
                    <span className={`rs-chat-online-pill${isConnected() ? ' live' : ''}`}>
                      {onlineCount} online
                    </span>
                  )}
                  <span className="rs-chat-socket-status">
                    {isConnected() ? '● Live' : '○ Offline'}
                  </span>
                </div>
              </div>
              <div className="rs-chat-mp-search-wrap">
                <input
                  type="text"
                  className="rs-chat-mp-search"
                  placeholder={middleMode === 'contacts' ? 'Search contacts' : 'Search'}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>

              {middleMode === 'chat' && (
                <div className="rs-chat-conv-list">
                  {pinned.length > 0 && (
                    <>
                      <div className="rs-chat-conv-pinned">Pinned</div>
                      {pinned.map(renderConvRow)}
                    </>
                  )}
                  {regular.map(renderConvRow)}
                  {filteredContacts.length === 0 && (
                    <p style={{ padding: 24, textAlign: 'center', color: '#999', fontSize: 13 }}>No conversations</p>
                  )}
                </div>
              )}

              {middleMode === 'contacts' && (
                <div className="rs-chat-contacts-body">
                  {platformAdminContact && (
                    <div className="rs-chat-contacts-group">
                      <button type="button" className="rs-chat-group-header" onClick={() => toggleGroup('admin')}>
                        <span className={`rs-chat-group-arrow${collapsedGroups.admin ? ' collapsed' : ''}`}>∨</span>
                        <span className="rs-chat-group-title">Platform Admin</span>
                      </button>
                      <div className={`rs-chat-group-list${collapsedGroups.admin ? ' collapsed' : ''}`}>
                        {renderContactRow(platformAdminContact)}
                      </div>
                    </div>
                  )}
                  {serviceProviders.length > 0 && (
                    <div className="rs-chat-contacts-group">
                      <button type="button" className="rs-chat-group-header" onClick={() => toggleGroup('sp')}>
                        <span className={`rs-chat-group-arrow${collapsedGroups.sp ? ' collapsed' : ''}`}>∨</span>
                        <span className="rs-chat-group-title">Service Providers</span>
                      </button>
                      <div className={`rs-chat-group-list${collapsedGroups.sp ? ' collapsed' : ''}`}>
                        {serviceProviders.map(renderContactRow)}
                      </div>
                    </div>
                  )}
                  <div className="rs-chat-contacts-group">
                    <button type="button" className="rs-chat-group-header" onClick={() => toggleGroup('contacts')}>
                      <span className={`rs-chat-group-arrow${collapsedGroups.contacts ? ' collapsed' : ''}`}>∨</span>
                      <span className="rs-chat-group-title">Contacts</span>
                    </button>
                    <div className={`rs-chat-group-list${collapsedGroups.contacts ? ' collapsed' : ''}`}>
                      {Object.keys(alphaContacts).sort().map((letter) => (
                        <React.Fragment key={letter}>
                          <div className="rs-chat-alpha-header">{letter}</div>
                          {alphaContacts[letter].map(renderContactRow)}
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="rs-chat-contact-detail show">
              <button type="button" className="rs-chat-cd-back" onClick={() => setDetailContact(null)}>
                <span>‹</span> Back
              </button>
              <div className="rs-chat-cd-body">
                <div className="rs-chat-cd-avatar-wrap">
                  <ChatAvatar contact={detailContact} size={80} className="rs-chat-cd-avatar-img" />
                  <span className={`rs-chat-cd-status-dot ${detailContact.online === 'online' ? 'online' : 'offline'}`} />
                </div>
                <span className={`rs-chat-cd-role-badge ${roleBadgeClass(detailContact.role)}`}>
                  {detailContact.role === 'ServiceProvider' ? 'Service' : detailContact.role === 'Admin' ? 'Admin' : detailContact.role}
                </span>
                <div className="rs-chat-cd-name">{convDisplayName(detailContact)}</div>
                <div className="rs-chat-cd-company">{detailContact.company || '—'}</div>
                <div className="rs-chat-cd-online-time">
                  {detailContact.online === 'online' ? 'Active now' : 'Offline'}
                </div>
                <button
                  type="button"
                  className="rs-chat-cd-start-btn"
                  onClick={() => loadChatHistory(detailContact)}
                >
                  Start Chat
                </button>
                <div className="rs-chat-cd-divider" />
                <div className="rs-chat-cd-meta">
                  <div className="rs-chat-cd-meta-row">
                    <span className="rs-chat-cd-meta-label">Company</span>
                    <span className="rs-chat-cd-meta-value">{detailContact.company || '—'}</span>
                  </div>
                  <div className="rs-chat-cd-meta-row">
                    <span className="rs-chat-cd-meta-label">Role</span>
                    <span className="rs-chat-cd-meta-value">{detailContact.role}</span>
                  </div>
                  {!canChat(detailContact) && (
                    <div className="rs-chat-cd-meta-row">
                      <span className="rs-chat-cd-meta-label">Chat</span>
                      <span className="rs-chat-cd-meta-value" style={{ color: '#ea580c' }}>Upgrade required</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Chat area */}
        {selectedContact ? (
          <div className={`rs-chat-main-wrap${selectedContact.isGroup && showGroupMembersPanel ? ' with-members' : ''}`}>
          <main className="rs-chat-area">
            <header className="rs-chat-header">
              <div className="rs-chat-header-left">
                <span className="rs-chat-header-name">{convDisplayName(selectedContact)}</span>
                <span className="rs-chat-header-status">
                  {selectedContact.isGroup
                    ? `${groupMembers.length || selectedContact.memberCount || 0} members`
                    : `${selectedContact.company} · ${selectedContact.role}`}
                  {!selectedContact.isGroup && selectedContact.online === 'online' && ' · Online'}
                </span>
              </div>
              <div className="rs-chat-header-right">
                {!selectedContact.isGroup && (
                  <>
                    <button type="button" className="rs-chat-header-btn" title="Voice call" disabled={startingCall} onClick={() => startCall('voice')}>
                      <RsIcon name="phoneCall" className="rs-chat-header-icon" size={18} aria-hidden />
                    </button>
                    <button type="button" className="rs-chat-header-btn" title="Video call" disabled={startingCall} onClick={() => startCall('video')}>
                      <RsIcon name="videoCamera" className="rs-chat-header-icon" size={18} aria-hidden />
                    </button>
                  </>
                )}
                {selectedContact.isGroup && (
                  <>
                    <button
                      type="button"
                      className={`rs-chat-header-btn${showGroupMembersPanel ? ' active' : ''}`}
                      title="Group members"
                      onClick={() => setShowGroupMembersPanel((v) => !v)}
                    >
                      <RsIcon name="users" className="rs-chat-header-icon" size={18} aria-hidden />
                    </button>
                    {isGroupOwner && (
                      <button
                        type="button"
                        className="rs-chat-header-btn"
                        title="Add members"
                        disabled={groupMembers.length >= MAX_GROUP_MEMBERS}
                        onClick={openAddMembersModal}
                      >
                        <RsIcon name="userPlus" className="rs-chat-header-icon" size={18} aria-hidden />
                      </button>
                    )}
                    {isGroupOwner && (
                      <button
                        type="button"
                        className="rs-chat-header-btn danger"
                        title="Dissolve group"
                        disabled={dissolvingGroup}
                        onClick={handleDissolveGroup}
                      >
                        <RsIcon name="trash" className="rs-chat-header-icon" size={18} aria-hidden />
                      </button>
                    )}
                  </>
                )}
                <button type="button" className={`rs-chat-header-btn${showChatSearch ? ' active' : ''}`} title="Search" onClick={() => setShowChatSearch((v) => !v)}>
                  <RsIcon name="search" size="md" />
                </button>
                <button type="button" className={`rs-chat-header-btn${isMuted ? ' active' : ''}`} title="Mute" onClick={toggleChatMute}>
                  <RsIcon name={isMuted ? 'bellOff' : 'bell'} size="md" />
                </button>
                <button type="button" className={`rs-chat-header-btn${isPinned ? ' active' : ''}`} title="Pin" onClick={togglePin}>
                  <RsIcon name={isPinned ? 'pin' : 'clipboard'} size="md" />
                </button>
                {!selectedContact.isGroup && (
                  <button
                    type="button"
                    className={`rs-chat-header-btn${peerBlockStatus.blockedByMe ? ' active danger' : ''}`}
                    title={peerBlockStatus.blockedByMe ? 'Unblock user' : 'Block user'}
                    disabled={blockActionLoading}
                    onClick={handleToggleBlock}
                  >
                    <RsIcon name="moderation" className="rs-chat-header-icon" size={18} aria-hidden />
                  </button>
                )}
                {!selectedContact.isGroup && (
                  <button
                    type="button"
                    className="rs-chat-header-btn"
                    title="Translate latest incoming message (or click a message bubble)"
                    onClick={translateLatestIncoming}
                  >
                    <RsIcon name="globe" size="md" />
                  </button>
                )}
              </div>
            </header>

            {selectedContact && !selectedContact.isGroup && peerBlockStatus.blocked && (
              <div className="rs-chat-block-banner">
                {peerBlockStatus.blockedByMe
                  ? 'You blocked this user. Unblock them to send messages or start calls.'
                  : 'You cannot send messages to this user.'}
              </div>
            )}

            {showChatSearch && (
              <div className="rs-chat-search-bar visible">
                <input
                  type="text"
                  placeholder="Search messages..."
                  value={chatSearchQuery}
                  onChange={(e) => setChatSearchQuery(e.target.value)}
                />
                <button type="button" className="rs-chat-header-btn" onClick={() => { setShowChatSearch(false); setChatSearchQuery('') }} aria-label="Close">
                  <RsIcon name="close" size="sm" />
                </button>
              </div>
            )}

            <div className="rs-chat-messages" ref={chatMessagesRef}>
              {filteredMessages.map((msg) => {
                const recalled = recalledMsgIds.includes(msg.id)
                if (msg.sender === 'system') {
                  return (
                    <div key={msg.id} className="rs-chat-msg-system">{msg.text}</div>
                  )
                }
                const msgContact = msg.sender === 'me'
                  ? { id: user?.id, name: user?.system_name, role: user?.role }
                  : selectedContact?.isGroup
                    ? { id: msg.senderId, name: msg.senderName || 'Member', role: 'Group' }
                    : selectedContact
                return (
                <div
                  key={msg.id}
                  data-msg-id={msg.id}
                  className={`rs-chat-msg-row${msg.sender === 'me' ? ' self' : ''}${selectedContact?.isGroup && msg.sender === 'them' ? ' group' : ''}`}
                >
                  <ChatAvatar
                    contact={msgContact}
                    size={32}
                    className="rs-chat-msg-avatar"
                  />
                  <div>
                    {selectedContact?.isGroup && msg.sender === 'them' && (
                      <div className="rs-chat-msg-sender">{msg.senderName}</div>
                    )}
                    <div
                      className={`rs-chat-msg-bubble${recalled ? ' recalled' : ''}${msgTranslations[msgTranslationKey(msg.id)] ? ' translated' : ''}${msg.messageType === 'file' ? ' file' : ''}${msg.isImage ? ' image' : ''}${msg.replyTo ? ' quoted' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation()
                        if (!recalled && msg.sender === 'them' && msg.messageType !== 'file') handleMessageTranslate(msg)
                      }}
                      onContextMenu={(e) => {
                        e.preventDefault()
                        if (!recalled) openMsgMenu(e, msg)
                      }}
                    >
                      {recalled ? (msg.sender === 'me' ? 'You recalled a message' : 'Message recalled') : (
                        <>
                          {msg.replyTo && (
                            <ChatQuoteBlock
                              quote={msg.replyTo}
                              variant="bubble"
                              self={msg.sender === 'me'}
                              onJump={scrollToQuotedMessage}
                            />
                          )}
                          {msg.messageType === 'file' ? (
                          msg.isImage && !msg.legacyFile ? (
                            <ChatImageAttachment
                              msg={msg}
                              variant="message"
                              onPreview={setImagePreview}
                              onDownload={handleChatFileDownload}
                            />
                          ) : (
                          <div className="rs-chat-msg-file">
                            <span className="rs-chat-msg-file-icon" aria-hidden><RsIcon name="files" size="sm" /></span>
                            <div className="rs-chat-msg-file-info">
                              <div className="rs-chat-msg-file-name">{msg.fileName || msg.text}</div>
                              {msg.legacyFile ? (
                                <span className="rs-chat-msg-file-legacy">Please ask the sender to resend the file</span>
                              ) : (
                                <button
                                  type="button"
                                  className="rs-chat-msg-file-btn"
                                  onClick={(e) => handleChatFileDownload(msg, e)}
                                >
                                  Download
                                </button>
                              )}
                            </div>
                          </div>
                          )
                        ) : msg.sender === 'them' ? (
                          <span className="rs-chat-msg-original">{msg.text}</span>
                        ) : (
                          <span className="rs-chat-msg-original">{msg.text}</span>
                        )}
                        </>
                      )}
                      {!recalled && msg.contentMasked && msg.sender === 'them' && (
                        <div className="rs-chat-msg-masked">Contact info masked</div>
                      )}
                      {!recalled && translatingMsgId === msg.id && (
                        <div className="rs-chat-msg-translating">Translating…</div>
                      )}
                      {!recalled && msgTranslations[msgTranslationKey(msg.id)]?.map((tr) => (
                        <div key={tr.code} className={`rs-chat-msg-translation${tr.error ? ' error' : ''}`}>
                          <span className="rs-chat-msg-translation-lang">{tr.label}</span>
                          <span className="rs-chat-msg-translation-text">{tr.text}</span>
                        </div>
                      ))}
                      {!recalled && (
                        <div className="rs-chat-msg-meta">
                          {readReceiptsEnabled && msg.sender === 'me' && msg.messageType !== 'system' && (
                            <span className={`rs-chat-msg-read-status ${msg.readStatus || 'unread'}`}>
                              {formatReadStatusLabel(msg, selectedContact?.isGroup)}
                            </span>
                          )}
                          <span className="rs-chat-msg-time">{msg.time}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )})}
              <div ref={messagesEndRef} />
            </div>

            <footer className="rs-chat-compose">
            {selectedContact && !canChat(selectedContact) && (
              <div className="rs-chat-paywall-banner">
                <div>
                  <strong>Membership upgrade required</strong>
                  <p>Free users cannot message GP/LP/Startup members. You can still contact Platform Admin and service providers for free.</p>
                </div>
                <button type="button" className="rs-chat-paywall-btn" onClick={() => setShowPaywall(true)}>
                  View plans
                </button>
              </div>
            )}

            <div className="rs-chat-input-area" ref={inputAreaRef}>
              <input
                ref={localFileInputRef}
                type="file"
                hidden
                onChange={handleLocalFileSelected}
              />
              <div className="rs-chat-toolbar">
                <button type="button" className="rs-chat-tool-btn" title="Emoji" onClick={() => { setShowFilePanel(false); setShowEmoji((v) => !v) }}>😊</button>
                <button type="button" className={`rs-chat-tool-btn${showFilePanel ? ' active' : ''}`} title="Send from Files" onClick={openFilePanel}>
                  <RsIcon name="folder" size="md" />
                </button>
                <button
                  type="button"
                  className={`rs-chat-tool-btn${uploadingLocalFile ? ' active' : ''}`}
                  title="Upload from computer"
                  disabled={uploadingLocalFile || !selectedContact}
                  onClick={openLocalFilePicker}
                >
                  <RsIcon name="upload" size="md" />
                </button>
                <button type="button" className="rs-chat-tool-btn" title="Screenshot conversation" onClick={openScreenshotModal}>
                  <RsIcon name="scissors" size="md" />
                </button>
              </div>
              {showFilePanel && (
                <div className="rs-chat-file-panel show">
                  <div className="rs-chat-file-panel-header">
                    <IconLabel name="folder" size="xs">Select from Files</IconLabel>
                    <button
                      type="button"
                      className="rs-chat-file-panel-upload"
                      disabled={uploadingLocalFile}
                      onClick={openLocalFilePicker}
                    >
                      <RsIcon name="upload" size="xs" />
                      Upload local
                    </button>
                    <button type="button" className="rs-chat-file-panel-close" onClick={() => setShowFilePanel(false)} aria-label="Close">
                      <RsIcon name="close" size="sm" />
                    </button>
                  </div>
                  {filesLoading ? (
                    <p style={{ padding: 16, color: '#888', fontSize: 13 }}>Loading files…</p>
                  ) : userFiles.length === 0 ? (
                    <p style={{ padding: 16, color: '#888', fontSize: 13 }}>No files in your document center</p>
                  ) : userFiles.map((f) => (
                    <div key={f.id} className="rs-chat-file-item">
                      <span className="rs-chat-file-icon"><RsIcon name={fileIcon(f.original_name || f.name)} size="sm" /></span>
                      <div className="rs-chat-file-info">
                        <div className="rs-chat-file-name">{f.original_name || f.name}</div>
                        <div className="rs-chat-file-size">{formatFileSize(f.size)}</div>
                      </div>
                      <button type="button" className="rs-chat-file-send-btn" onClick={() => sendFileMessage(f)}>Send</button>
                    </div>
                  ))}
                </div>
              )}
              {showEmoji && (
                <div className="rs-chat-emoji-panel show">
                  <div className="rs-chat-emoji-grid">
                    {EMOJIS.map((e) => (
                      <button key={e} type="button" className="rs-chat-emoji-item" onClick={() => insertEmoji(e)}>{e}</button>
                    ))}
                  </div>
                </div>
              )}
              <div className="rs-chat-input-wrap">
                {quoteDraft && (
                  <ChatQuoteBlock
                    quote={quoteDraft}
                    variant="compose"
                    self={quoteDraft.sender_id === user?.id}
                    onClose={clearQuoteDraft}
                  />
                )}
                {pendingAttachments.length > 0 && (
                  <div className="rs-chat-pending-attachments">
                    {pendingAttachments.map((att) => (
                      <div key={att.id} className={`rs-chat-pending-item${att.error ? ' error' : ''}`}>
                        <div className="rs-chat-pending-thumb-wrap">
                          <img src={att.previewUrl} alt={att.filename} className="rs-chat-pending-thumb" />
                          {att.uploading && <span className="rs-chat-pending-status">Uploading…</span>}
                          {att.error && <span className="rs-chat-pending-status error">{att.error}</span>}
                        </div>
                        <button
                          type="button"
                          className="rs-chat-pending-remove"
                          onClick={() => removePendingAttachment(att.id)}
                          aria-label="Remove image"
                        >
                          <RsIcon name="close" size="xs" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="rs-chat-input-row">
                <textarea
                  ref={inputRef}
                  className="rs-chat-msg-input"
                  rows={3}
                  value={inputMessage}
                  placeholder={canChat(selectedContact) ? 'Type a message... (paste image to attach)' : 'Upgrade to send messages'}
                  onPaste={handlePaste}
                  onChange={(e) => {
                    setInputMessage(e.target.value)
                    e.target.style.height = 'auto'
                    e.target.style.height = `${Math.min(e.target.scrollHeight, 120)}px`
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault()
                      handleSend()
                    }
                    if (selectedContact && isConnected()) {
                      const typing = e.key !== 'Enter'
                      if (selectedContact.isGroup) {
                        emitTyping({ conversationId: selectedContact.id }, typing)
                      } else {
                        emitTyping({ receiverId: selectedContact.id }, typing)
                      }
                    }
                  }}
                />
                <button
                  type="button"
                  className="rs-chat-send-btn"
                  disabled={!canSendMessage}
                  onClick={handleSend}
                  title="Send"
                >
                  ↑
                </button>
                </div>
              </div>
            </div>
            </footer>
          </main>

          {selectedContact.isGroup && showGroupMembersPanel && (
            <aside className="rs-chat-group-members-panel">
              <div className="rs-chat-gmp-header">
                <h3 className="rs-chat-gmp-title">Group Members</h3>
                <span className="rs-chat-gmp-count">
                  {groupMembersLoading
                    ? '…'
                    : `${groupMembers.length} / ${MAX_GROUP_MEMBERS}`}
                </span>
              </div>
              <div className="rs-chat-gmp-body">
                {groupMembersLoading && (
                  <p className="rs-chat-gmp-empty">Loading members…</p>
                )}
                {!groupMembersLoading && groupMembers.length === 0 && (
                  <p className="rs-chat-gmp-empty">No members found</p>
                )}
                {!groupMembersLoading && groupMembers.length > 0 && (
                  <div className="rs-chat-gmp-grid">
                    {groupMembers.map((member) => {
                      const isMe = member.id === user?.id
                      return (
                        <button
                          key={member.id}
                          type="button"
                          className={`rs-chat-gmp-member${isMe ? ' is-me' : ''}`}
                          onClick={() => openMemberPrivateChat(member)}
                          disabled={isMe}
                          title={isMe ? 'You' : `Chat with ${member.name}`}
                        >
                          <ChatAvatar contact={member} size={48} className="rs-chat-gmp-avatar" />
                          <span className="rs-chat-gmp-name">{isMe ? 'You' : member.name}</span>
                          <span className={`rs-chat-gmp-role role-${roleBadgeClass(member.role)}`}>
                            {member.memberRole === 'owner' ? 'Owner · ' : ''}
                            {member.role === 'ServiceProvider' ? 'Service' : member.role}
                          </span>
                        </button>
                      )
                    })}
                  </div>
                )}
              </div>
              <p className="rs-chat-gmp-hint">
                {isGroupOwner ? 'You are the group owner · Click a member to chat privately' : 'Click a member to start a private chat'}
              </p>
              <div className="rs-chat-gmp-footer">
                {isGroupOwner && groupMembers.length < MAX_GROUP_MEMBERS && (
                  <button
                    type="button"
                    className="rs-chat-gmp-add-btn"
                    onClick={openAddMembersModal}
                  >
                    <RsIcon name="userPlus" size={16} aria-hidden />
                    Add members
                  </button>
                )}
                <button
                  type="button"
                  className="rs-chat-gmp-leave-btn"
                  disabled={leavingGroup || dissolvingGroup}
                  onClick={handleLeaveGroup}
                >
                  {leavingGroup ? 'Leaving…' : 'Leave Group'}
                </button>
                {isGroupOwner && (
                  <button
                    type="button"
                    className="rs-chat-gmp-dissolve-btn"
                    disabled={dissolvingGroup || leavingGroup}
                    onClick={handleDissolveGroup}
                  >
                    {dissolvingGroup ? 'Dissolving…' : 'Dissolve Group'}
                  </button>
                )}
              </div>
            </aside>
          )}
          </div>
        ) : (
          <main className="rs-chat-area empty">
            <div style={{ textAlign: 'center' }}>
              <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'center' }}><RsIcon name="chat" size={48} /></div>
              <p>Select a conversation to start chatting</p>
            </div>
          </main>
        )}
      </div>

      {ctxMenu && (
        <div
          className="rs-chat-ctx-menu"
          style={{
            left: Math.min(ctxMenu.x, window.innerWidth - 160),
            top: Math.min(ctxMenu.y, window.innerHeight - 280),
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {(ctxMenu.msg.sender === 'me' ? SELF_CTX_ITEMS : OTHER_CTX_ITEMS).map((item, i) =>
            item.sep ? (
              <div key={`sep-${i}`} className="rs-chat-ctx-sep" />
            ) : (
              <button
                key={item.action}
                type="button"
                className={`rs-chat-ctx-item${item.danger ? ' danger' : ''}`}
                onClick={() => handleCtxAction(item.action)}
              >
                <IconLabel name={item.icon} size="xs">{item.label}</IconLabel>
              </button>
            )
          )}
        </div>
      )}

      {imagePreview && (
        <div className="rs-chat-image-lightbox" onClick={() => setImagePreview(null)}>
          <div className="rs-chat-image-lightbox-inner" onClick={(e) => e.stopPropagation()}>
            <ModalClose
              className="rs-chat-image-lightbox-close"
              onClick={() => setImagePreview(null)}
            />
            <img
              src={imagePreview.src}
              alt={imagePreview.msg?.fileName || imagePreview.msg?.text || 'Image preview'}
              className="rs-chat-image-lightbox-img"
            />
            <div className="rs-chat-image-lightbox-actions">
              <button
                type="button"
                className="rs-chat-modal-btn primary"
                onClick={() => handleChatFileDownload(imagePreview.msg)}
              >
                Download
              </button>
              <button
                type="button"
                className="rs-chat-modal-btn secondary"
                onClick={() => setImagePreview(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {expandText != null && (
        <div className="rs-chat-modal-overlay" onClick={() => setExpandText(null)}>
          <div className="rs-chat-modal" onClick={(e) => e.stopPropagation()}>
            <div className="rs-chat-modal-header">
              <span>Message</span>
              <ModalClose className="rs-chat-modal-close" onClick={() => setExpandText(null)} />
            </div>
            <div className="rs-chat-modal-body">{expandText}</div>
          </div>
        </div>
      )}

      {showScreenshot && (
        <div className="rs-chat-modal-overlay" onClick={closeScreenshotModal}>
          <div className="rs-chat-modal" onClick={(e) => e.stopPropagation()}>
            <div className="rs-chat-modal-header">
              <span>Screenshot</span>
              <ModalClose className="rs-chat-modal-close" onClick={closeScreenshotModal} />
            </div>
            <div style={{ background: '#0f172a', borderRadius: 8, minHeight: 160, maxHeight: 360, overflow: 'auto', border: '1px solid #334155', marginBottom: 14 }}>
              {screenshotCapturing && (
                <div style={{ padding: 48, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>Capturing messages…</div>
              )}
              {!screenshotCapturing && screenshotPreview && (
                <img src={screenshotPreview} alt="Chat screenshot preview" style={{ display: 'block', width: '100%', height: 'auto' }} />
              )}
            </div>
            <div className="rs-chat-modal-actions">
              <button
                type="button"
                className="rs-chat-modal-btn primary"
                disabled={!screenshotPreview || screenshotSaving}
                onClick={saveScreenshotToFiles}
              >
                {screenshotSaving ? 'Saving…' : 'Save to Files'}
              </button>
              <button type="button" className="rs-chat-modal-btn secondary" onClick={closeScreenshotModal}>Close</button>
            </div>
          </div>
        </div>
      )}

      {showGroupModal && (
        <div className="rs-chat-modal-overlay" onClick={closeGroupModal}>
          <div className="rs-chat-modal" onClick={(e) => e.stopPropagation()}>
            <div className="rs-chat-modal-header">
              <span>
                {groupModalMode === 'add'
                  ? 'Add Members'
                  : (matchDeal ? 'Deal Matchmaking Group' : 'Create Group')}
              </span>
              <ModalClose className="rs-chat-modal-close" onClick={closeGroupModal} />
            </div>
            {groupModalMode === 'add' && (
              <p className="rs-chat-group-modal-hint">
                {convDisplayName(selectedContact)} · {groupMembers.length}/{MAX_GROUP_MEMBERS} members · {groupPickLimit} more can be added
              </p>
            )}
            {groupModalMode === 'create' && matchDeal && (
              <p style={{ fontSize: 12, color: '#94a3b8', margin: '0 0 10px', lineHeight: 1.5 }}>
                Deal: <strong style={{ color: '#e2e8f0' }}>{matchDeal.title}</strong>
                {matchDeal.gp_name ? ` · GP: ${matchDeal.gp_name}` : ''}
                <br />
                Select GP and LP (or other parties), then create the group to facilitate introductions.
              </p>
            )}
            {groupModalMode === 'create' && (
              <input
                className="rs-chat-mp-search"
                style={{ width: '100%', marginBottom: 8 }}
                placeholder="Group name"
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
              />
            )}
            <input
              className="rs-chat-group-member-search"
              placeholder="Search by name or company"
              value={groupMemberSearch}
              onChange={(e) => setGroupMemberSearch(e.target.value)}
            />
            <p className="rs-chat-group-pick-meta" style={{ margin: '0 0 8px', padding: '0 2px' }}>
              Selected {groupPickIds.length}
              {groupModalMode === 'add' ? ` / ${groupPickLimit} available` : ` / ${groupPickLimit} (max ${MAX_GROUP_MEMBERS} incl. you)`}
            </p>
            <div className="rs-chat-group-list-modal">
              {groupPickCandidates.length === 0 && (
                <p className="rs-chat-group-search-empty">No matching members</p>
              )}
              {groupPickCandidates.map((c) => {
                const id = String(c.id)
                const atLimit = !groupPickIds.includes(id) && groupPickIds.length >= groupPickLimit
                return (
                  <label key={c.id} className={`rs-chat-group-pick${atLimit ? ' disabled' : ''}`}>
                    <input
                      type="checkbox"
                      checked={groupPickIds.includes(id)}
                      disabled={atLimit}
                      onChange={(e) => toggleGroupPick(id, e.target.checked)}
                    />
                    <ChatAvatar contact={c} size={28} />
                    <span className="rs-chat-group-pick-text">
                      <span className="rs-chat-group-pick-name">{convDisplayName(c)}</span>
                      {(c.company || c.role) && (
                        <span className="rs-chat-group-pick-meta">
                          {[c.company, c.role === 'ServiceProvider' ? 'Service' : c.role].filter(Boolean).join(' · ')}
                        </span>
                      )}
                    </span>
                  </label>
                )
              })}
            </div>
            <div className="rs-chat-modal-actions">
              <button
                type="button"
                className="rs-chat-modal-btn primary"
                onClick={groupModalMode === 'add' ? addMembersToGroup : createGroup}
                disabled={creatingGroup}
              >
                {creatingGroup
                  ? (groupModalMode === 'add' ? 'Adding…' : 'Creating…')
                  : (groupModalMode === 'add' ? 'Add to Group' : (matchDeal ? 'Create Matchmaking Group' : 'Create'))}
              </button>
              <button type="button" className="rs-chat-modal-btn secondary" onClick={closeGroupModal} disabled={creatingGroup}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {showForwardModal && forwardDraft && (
        <div className="rs-chat-modal-overlay" onClick={() => setShowForwardModal(false)}>
          <div className="rs-chat-modal" onClick={(e) => e.stopPropagation()}>
            <div className="rs-chat-modal-header">
              <span>↪ Forward message</span>
              <ModalClose className="rs-chat-modal-close" onClick={() => setShowForwardModal(false)} />
            </div>
            <div className="rs-chat-modal-body" style={{ marginBottom: 12, fontSize: 13, color: '#ccc' }}>
              {forwardDraft.text.slice(0, 200)}{forwardDraft.text.length > 200 ? '…' : ''}
            </div>
            <div className="rs-chat-group-list-modal" style={{ maxHeight: 280 }}>
              {contacts.filter((c) => !c.isGroup && c.id !== forwardDraft.from?.id).slice(0, 40).map((c) => (
                <button
                  key={c.id}
                  type="button"
                  className="rs-chat-group-pick"
                  style={{ width: '100%', border: 'none', background: 'transparent', cursor: 'pointer', textAlign: 'left' }}
                  onClick={() => handleForwardTo(c)}
                >
                  <ChatAvatar contact={c} size={28} />
                  <span style={{ fontSize: 13, color: '#ddd' }}>{convDisplayName(c)}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      <TrtcCallOverlay
        callState={callState}
        incomingCall={incomingCall}
        muted={muted}
        cameraOff={cameraOff}
        onAccept={acceptIncomingCall}
        onDecline={declineIncomingCall}
        onHangUp={hangUpCall}
        onToggleMute={toggleMute}
        onToggleCamera={toggleCamera}
        accepting={acceptingCall}
      />
    </PageShell>
  )
}
